import { ScrollMode, Viewer, Worker } from '@react-pdf-viewer/core';
import { scrollModePlugin } from '@react-pdf-viewer/scroll-mode';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { FC, useEffect } from 'react';

import { IconButton, Typography } from '@mui/material';

import placeholderImage from '@/assets/adv_space_placeholder_large.png';
import { ImageHandlers } from '@/shared/lib/hooks/useImagePanZoom';
import { isPlaceholderFile } from '@/shared/lib/isPlaceholderFile';

import { FileData } from './FileCard';
import {
  CarouselContainer,
  ControlsOverlayPdfImage,
  ImageBoxContainer,
  InnerContainer,
  LeftButton,
  LeftSideContainer,
  PdfContainer,
  RightActionsColumn,
  RightButton,
  StyledChevronLeft,
  StyledChevronRight,
  TitleOverlay,
} from './ImageSlider.styles';

const ExpandIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
    <path
      d="M5.81543 7.88411C6.05945 7.64011 6.45513 7.64023 6.69922 7.88411C6.94329 8.1282 6.9433 8.52383 6.69922 8.7679L2.13379 13.3333H5.45329C5.79829 13.3335 6.07829 13.6133 6.07829 13.9583C6.07829 14.3034 5.79829 14.5831 5.45329 14.5833H0.625C0.279822 14.5833 0 14.3035 0 13.9583V9.13086C0 8.78568 0.279822 8.50586 0.625 8.50586C0.970178 8.50586 1.25 8.78568 1.25 9.13086V12.4495L5.81543 7.88411ZM14.5833 5.45247C14.5833 5.79765 14.3035 6.07747 13.9583 6.07747C13.6132 6.07747 13.3333 5.79765 13.3333 5.45247V2.13379L8.7679 6.69922C8.52388 6.94322 8.1282 6.94311 7.88411 6.69922C7.64004 6.45513 7.64003 6.0595 7.88411 5.81543L12.4495 1.25H9.13005C8.78504 1.24979 8.50505 0.970051 8.50505 0.625C8.50505 0.279949 8.78504 0.0002065 9.13005 0H13.9583C14.3035 0 14.5833 0.279822 14.5833 0.625V5.45247Z"
      fill="black"
    />
  </svg>
);

const ZoomInCustomIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M15.4414 9.5332C15.4413 6.27018 12.7962 3.625 9.5332 3.625C6.27022 3.62507 3.62507 6.27022 3.625 9.5332C3.625 12.7962 6.27018 15.4413 9.5332 15.4414C12.7963 15.4414 15.4414 12.7963 15.4414 9.5332ZM8.87484 11.9998V10.1248H6.99984C6.65473 10.1247 6.37484 9.84496 6.37484 9.49984C6.37493 9.15479 6.65479 8.87493 6.99984 8.87484H8.87484V6.99984C8.87493 6.65479 9.15479 6.37493 9.49984 6.37484C9.84496 6.37484 10.1247 6.65473 10.1248 6.99984V8.87484H11.9998C12.345 8.87484 12.6247 9.15473 12.6248 9.49984C12.6248 9.84502 12.345 10.1248 11.9998 10.1248H10.1248V11.9998C10.1248 12.345 9.84502 12.6248 9.49984 12.6248C9.15473 12.6247 8.87484 12.345 8.87484 11.9998ZM16.6914 9.5332C16.6914 11.3052 16.0457 12.9251 14.9792 14.1751L17.4344 16.5506C17.6825 16.7906 17.689 17.1863 17.4491 17.4344C17.2091 17.6825 16.8134 17.6891 16.5653 17.4491L14.0897 15.054C12.8518 16.0768 11.2643 16.6914 9.5332 16.6914C5.57982 16.6913 2.375 13.4866 2.375 9.5332C2.37507 5.57987 5.57987 2.37507 9.5332 2.375C13.4866 2.375 16.6913 5.57982 16.6914 9.5332Z"
      fill="black"
    />
  </svg>
);

const ZoomOutCustomIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M15.4414 9.5332C15.4413 6.27018 12.7962 3.625 9.5332 3.625C6.27022 3.62507 3.62507 6.27022 3.625 9.5332C3.625 12.7962 6.27018 15.4413 9.5332 15.4414C12.7963 15.4414 15.4414 12.7963 15.4414 9.5332ZM11.9998 8.87484C12.345 8.87484 12.6247 9.15473 12.6248 9.49984C12.6248 9.84502 12.345 10.1248 11.9998 10.1248H6.99984C6.65473 10.1247 6.37484 9.84496 6.37484 9.49984C6.37493 9.15479 6.65479 8.87493 6.99984 8.87484H11.9998ZM16.6914 9.5332C16.6914 11.3052 16.0457 12.9251 14.9792 14.1751L17.4344 16.5506C17.6825 16.7906 17.6891 17.1863 17.4491 17.4344C17.2091 17.6825 16.8134 17.6891 16.5653 17.4491L14.0897 15.054C12.8518 16.0768 11.2643 16.6914 9.5332 16.6914C5.57982 16.6913 2.375 13.4866 2.375 9.5332C2.37507 5.57987 5.57987 2.37507 9.5332 2.375C13.4866 2.375 16.6913 5.57982 16.6914 9.5332Z"
      fill="black"
    />
  </svg>
);

const DownloadCustomIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M16.2296 16.5381C16.4765 16.297 16.8722 16.302 17.1134 16.5487C17.3545 16.7955 17.3503 17.1913 17.1037 17.4325C16.6481 17.8773 16.0327 18.125 15.3947 18.125H4.60529C3.96729 18.125 3.35185 17.8773 2.8963 17.4325C2.64967 17.1913 2.64547 16.7955 2.88654 16.5487C3.12773 16.302 3.52348 16.297 3.77033 16.5381C3.9893 16.7519 4.28938 16.875 4.60529 16.875H15.3947C15.7106 16.875 16.0107 16.7519 16.2296 16.5381ZM9.3758 2.5C9.3758 2.15489 9.6557 1.8751 10.0008 1.875C10.346 1.875 10.6258 2.15482 10.6258 2.5V11.0221L13.6873 8.19092C13.9407 7.95651 14.3359 7.97172 14.5703 8.2251C14.8046 8.47843 14.7893 8.87367 14.5361 9.10807L10.4256 12.911C10.1861 13.1325 9.81637 13.1324 9.57681 12.911L5.46629 9.10807C5.21299 8.87375 5.19714 8.47849 5.4313 8.2251C5.6657 7.97172 6.0617 7.95651 6.31509 8.19092L9.3758 11.0213V2.5Z"
      fill="black"
    />
  </svg>
);

const DeleteCustomIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M14.3749 6.02946C14.3749 5.87523 14.2782 5.79349 14.1991 5.77555L14.1666 5.7723H5.83325L5.8007 5.77555C5.72164 5.79349 5.62492 5.87523 5.62492 6.02946V15.7357C5.62511 16.3991 6.12457 16.875 6.66659 16.875H13.3333C13.8753 16.875 14.3747 16.3991 14.3749 15.7357V6.02946ZM11.8749 3.38216C11.8748 3.20602 11.7484 3.125 11.6666 3.125H8.33325C8.2514 3.125 8.12501 3.20602 8.12492 3.38216V4.26432C8.12492 4.44059 8.25137 4.5223 8.33325 4.5223H11.6666C11.7485 4.5223 11.8749 4.44059 11.8749 4.26432V3.38216ZM13.1249 4.26432C13.1249 4.35159 13.1164 4.43798 13.1021 4.5223H16.6666C17.0118 4.5223 17.2916 4.80212 17.2916 5.1473C17.2915 5.49237 17.0117 5.7723 16.6666 5.7723H15.6021C15.6164 5.85647 15.6249 5.94236 15.6249 6.02946V15.7357C15.6247 17.0212 14.632 18.125 13.3333 18.125H6.66659C5.36788 18.125 4.37512 17.0212 4.37492 15.7357V6.02946C4.37492 5.94236 4.38347 5.85647 4.39771 5.7723H3.33325C2.98815 5.7723 2.70838 5.49237 2.70825 5.1473C2.70825 4.80212 2.98807 4.5223 3.33325 4.5223H6.8977C6.88342 4.43798 6.87492 4.35159 6.87492 4.26432V3.38216C6.87502 2.58389 7.49473 1.875 8.33325 1.875H11.6666C12.5051 1.875 13.1248 2.58389 13.1249 3.38216V4.26432Z"
      fill="black"
    />
  </svg>
);

const RotateCustomIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M3.95842 9.99992C3.95842 6.64493 6.5911 3.95825 9.79826 3.95825C11.91 3.95832 13.7666 5.12044 14.7942 6.87492H13.4343C13.0892 6.87497 12.8093 7.15477 12.8093 7.49992C12.8093 7.84506 13.0892 8.12487 13.4343 8.12492H16.6667C17.0119 8.12492 17.2917 7.8451 17.2917 7.49992V4.16659C17.2917 3.82141 17.0119 3.54159 16.6667 3.54159C16.3216 3.54159 16.0417 3.82141 16.0417 4.16659V6.87492H15.984C16.0422 6.7109 16.0351 6.52432 15.9433 6.3606C14.7228 4.18484 12.4327 2.70833 9.79826 2.70825C5.86477 2.70825 2.70842 5.99111 2.70842 9.99992C2.70842 14.0087 5.86477 17.2916 9.79826 17.2916C12.9534 17.2915 15.6113 15.1757 16.5365 12.2729C16.6412 11.9441 16.4592 11.5923 16.1305 11.4875C15.8016 11.3829 15.4499 11.5648 15.3451 11.8936C14.5737 14.3138 12.3725 16.0415 9.79826 16.0416C6.5911 16.0416 3.95842 13.3549 3.95842 9.99992Z"
      fill="black"
    />
  </svg>
);

interface ImagePdfPreviewProps {
  selectedFile: FileData | null;
  rotation: number;
  zoom: number;
  offsetX?: number;
  offsetY?: number;
  imageHandlers?: ImageHandlers;
  hasMultipleFiles: boolean;
  hasDeleteOption?: boolean;
  onPrev: () => void;
  onNext: () => void;
  onRotate: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onDownload: () => void;
  onExpand: () => void;
  onDelete: () => void;
}

const ImagePdfPreview: FC<ImagePdfPreviewProps> = ({
  selectedFile,
  rotation,
  zoom,
  offsetX = 0,
  offsetY = 0,
  imageHandlers,
  hasMultipleFiles,
  hasDeleteOption = true,
  onPrev,
  onNext,
  onRotate,
  onZoomIn,
  onZoomOut,
  onDownload,
  onExpand,
  onDelete,
}) => {
  const zoomPluginInstance = zoomPlugin();
  const { zoomTo } = zoomPluginInstance;

  const scrollModePluginInstance = scrollModePlugin();
  const { switchScrollMode } = scrollModePluginInstance;

  const isPdf =
    selectedFile?.fileType === 'PDF' || selectedFile?.realFileName?.toLowerCase().endsWith('.pdf');
  const pdfReady = isPdf && selectedFile?.thumbnail?.startsWith('blob:');

  const isVideo = /\.(mp4|webm|ogg|mov|avi)$/i.test(selectedFile?.realFileName ?? '');
  const videoReady = isVideo && !!selectedFile?.thumbnail;

  const isText = /\.(txt|csv|log|md)$/i.test(selectedFile?.realFileName ?? '');
  const textReady = isText && selectedFile?.thumbnail?.startsWith('blob:');

  const isPlaceholder = isPlaceholderFile(selectedFile);

  useEffect(() => {
    if (pdfReady) {
      zoomTo(zoom);
    }
  }, [zoom, pdfReady, zoomTo]);

  const controls = [
    { icon: <ExpandIcon />, label: 'Expand', onClick: onExpand, show: !isPlaceholder },
    { icon: <DownloadCustomIcon />, label: 'Download', onClick: onDownload, show: !isPlaceholder },
    {
      icon: <DeleteCustomIcon />,
      label: 'Delete',
      onClick: onDelete,
      show: hasDeleteOption && !isPlaceholder,
    },
    {
      icon: <RotateCustomIcon />,
      label: 'Rotate',
      onClick: onRotate,
      show: !isPdf && !isVideo && !isText && !isPlaceholder,
    },
    {
      icon: <ZoomInCustomIcon />,
      label: 'Zoom In',
      onClick: onZoomIn,
      show: !isVideo && !isText && !isPlaceholder,
    },
    {
      icon: <ZoomOutCustomIcon />,
      label: 'Zoom Out',
      onClick: onZoomOut,
      show: !isVideo && !isText && !isPlaceholder,
    },
  ];

  const visibleControls = controls.filter((item) => item.show);

  return (
    <LeftSideContainer>
      <InnerContainer>
        <CarouselContainer>
          <LeftButton
            onClick={onPrev}
            disabled={!hasMultipleFiles}
            sx={{ visibility: hasMultipleFiles ? 'visible' : 'hidden' }}
          >
            <StyledChevronLeft />
          </LeftButton>

          <ImageBoxContainer
            onDoubleClick={!isVideo && !isText && !isPlaceholder ? onZoomIn : undefined}
          >
            {textReady ? (
              <iframe
                src={selectedFile!.thumbnail}
                title={selectedFile!.realFileName}
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            ) : pdfReady ? (
              <PdfContainer>
                <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
                  <Viewer
                    fileUrl={selectedFile.thumbnail}
                    plugins={[zoomPluginInstance, scrollModePluginInstance]}
                    defaultScale={zoom}
                    scrollMode={ScrollMode.Vertical}
                    onDocumentLoad={() => {
                      switchScrollMode(ScrollMode.Vertical);
                    }}
                  />
                </Worker>
              </PdfContainer>
            ) : videoReady ? (
              <video
                key={selectedFile!.thumbnail}
                src={selectedFile!.thumbnail}
                controls
                style={{
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  transform: `translate(${offsetX}px, ${offsetY}px) rotate(${rotation}deg) scale(${zoom})`,
                  transformOrigin: 'center center',
                  cursor: zoom > 1 ? 'grab' : 'default',
                }}
                onMouseDown={imageHandlers?.onMouseDown}
                onMouseMove={imageHandlers?.onMouseMove}
                onMouseUp={imageHandlers?.onMouseUp}
                onMouseLeave={imageHandlers?.onMouseLeave}
                onWheel={imageHandlers?.onWheel}
              />
            ) : (
              <img
                src={selectedFile?.thumbnail || placeholderImage}
                alt="preview"
                style={{
                  width: 'auto',
                  height: '100%',
                  maxWidth: '100%',
                  objectFit: 'contain',
                  transform: `translate(${offsetX}px, ${offsetY}px) rotate(${rotation}deg) scale(${zoom})`,
                  transformOrigin: 'center center',
                  cursor: zoom > 1 ? 'grab' : 'default',
                }}
                onMouseDown={!isPlaceholder ? imageHandlers?.onMouseDown : undefined}
                onMouseMove={!isPlaceholder ? imageHandlers?.onMouseMove : undefined}
                onMouseUp={!isPlaceholder ? imageHandlers?.onMouseUp : undefined}
                onMouseLeave={!isPlaceholder ? imageHandlers?.onMouseLeave : undefined}
                onWheel={!isPlaceholder ? imageHandlers?.onWheel : undefined}
                draggable={false}
              />
            )}

            {selectedFile && !isPdf && !isVideo && (
              <TitleOverlay>
                <Typography sx={{ color: '#010203', fontSize: '0.875rem', fontWeight: 500 }} noWrap>
                  {selectedFile.realFileName}
                </Typography>
              </TitleOverlay>
            )}
          </ImageBoxContainer>

          <RightActionsColumn>
            <RightButton
              onClick={onNext}
              disabled={!hasMultipleFiles}
              sx={{ visibility: hasMultipleFiles ? 'visible' : 'hidden' }}
            >
              <StyledChevronRight />
            </RightButton>

            {visibleControls.length > 0 && (
              <ControlsOverlayPdfImage>
                {visibleControls.map((item, index) => (
                  <IconButton
                    key={index}
                    size="small"
                    onClick={item.onClick}
                    sx={{ color: 'rgba(0,0,0,0.8)', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
                    title={item.label}
                  >
                    {item.icon}
                  </IconButton>
                ))}
              </ControlsOverlayPdfImage>
            )}
          </RightActionsColumn>
        </CarouselContainer>
      </InnerContainer>
    </LeftSideContainer>
  );
};

export default ImagePdfPreview;
